/**
 * WHAT A CROSS-TASK ANSWER REFERENCE CAN POINT AT.
 *
 * A teacher writing task 7 points at what this same student answered in task 2, and it appears in
 * their prose. This file is the half that decides what is pointable: the address, which blocks
 * offer one, and which PART of an answer a reference names.
 *
 * ## A reference addresses a FACET, not a block
 *
 * Several blocks answer with more than one thing, and the teacher may want a part or the whole.
 * GPS is the clearest: latitude, longitude, or the pair. A dual slider is the same shape — the
 * low, the high, or "3 to 7". A numerical answer with units is the number, the unit, or both.
 *
 * ## Arity is per INSTANCE, never per type
 *
 * A slider has a second handle only if that was switched on; a fill-in-the-blank has three blanks
 * only if three were made; a numerical answer has a unit only if it asks for one. So the facets
 * are computed from the block's own data every time, and there is deliberately no static
 * per-type table to fall out of step with what the teacher actually built.
 *
 * ## Why some blocks offer nothing
 *
 * Not an oversight in any of the four cases, and the picker shows them with the reason rather
 * than hiding them — a teacher who cannot see why a block is missing goes hunting for it.
 */
import { deriveGraph } from './graph.js';
import { starvedBlockCauses } from './positive-reachability.js';
import { uncertaintyAsked } from './numerical.js';
/** The facets that are not a FITB blank id are these, so they read the same across blocks. */
export const ANSWER_FACET = {
    /** The whole answer, for a block that answers with one thing. */
    value: 'value',
    /** The parts and the whole, for the blocks that answer with more than one. */
    lat: 'lat',
    lng: 'lng',
    low: 'low',
    high: 'high',
    unit: 'unit',
    /** The student's reported uncertainty, where the question asked for one. */
    uncertainty: 'uncertainty',
    /** The word above a scale's point, which names the ordinal rather than qualifying it. */
    label: 'label',
    combined: 'combined',
};
const num = (id) => ({ id, kind: 'number' });
const txt = (id) => ({ id, kind: 'text' });
/** Does any option carry an image or a clip? One is enough to rule the block out. */
function hasMediaOption(data) {
    const options = data.options;
    return !!options?.some(o => !!o.media);
}
/**
 * Which facets this block offers, or why it offers none.
 *
 * `data` is the block's own stored data — read, never written. Unknown block types answer
 * `noAnswer` rather than throwing: a type this file has not met is one that cannot be referenced
 * yet, which is the honest answer and not a crash.
 */
export function answerRefEligibility(type, data) {
    const no = (reason) => ({ referenceable: false, reason });
    // Checked before the per-type facets, because it is true of every type and the teacher's
    // question is "why can I not use this block", not "which of its parts are missing".
    if (data.gradingMode === 'optional')
        return no('optional');
    switch (type) {
        case 'keyword':
            return { referenceable: true, facets: [txt(ANSWER_FACET.value)] };
        /*
         * A SCALE ANSWERS TWICE: the ordinal, and the word above it.
         *
         * `value` alone was the whole offering, which is the unnatural reading of a Likert item —
         * a teacher quoting one means "you said Good" far more often than "you said 4". Labels are
         * optional and may be anchors at the two ends only, so the text facets appear only when
         * there is at least one, and a bare numeric scale is offered exactly what it was before.
         */
        case 'scale': {
            const labelled = (Array.isArray(data.values) ? data.values : [])
                .some(v => typeof v.label === 'string'
                && v.label.trim() !== '');
            return {
                referenceable: true,
                facets: labelled
                    ? [num(ANSWER_FACET.value), txt(ANSWER_FACET.label), txt(ANSWER_FACET.combined)]
                    : [num(ANSWER_FACET.value)],
            };
        }
        case 'compass':
        case 'tilt':
            return { referenceable: true, facets: [num(ANSWER_FACET.value)] };
        case 'mcq':
        case 'dropdown':
            // The chosen option's own text, captured as the student saw it. A multi-select yields one
            // JOINED string and never per-selection facets: a fill-in-the-blank can be addressed
            // blank-by-blank because the teacher made those slots, but a multi-select is a subset the
            // STUDENT chose, so "the third one picked" is empty for anyone who picked two.
            if (hasMediaOption(data))
                return no('media');
            /*
             * EQUATION OPTIONS ARE FINE, and used to be refused here.
             *
             * The reason given was that the student saw a rendered formula while `text` held only
             * the teacher's internal label. That was wrong: `mcqOptionDisplayText` and Dropdown's
             * twin EVALUATE the formula and show the result, "a plain formatted number, never the
             * formula string itself". So what the student saw is a number — which is the best thing
             * a recall can carry, not the worst. `selectedTexts` records what they saw, so nothing
             * else has to change.
             */
            return { referenceable: true, facets: [txt(ANSWER_FACET.value)] };
        case 'numerical': {
            // The unit is a facet only when one was asked for. `display` shows a unit the teacher
            // wrote, so there is nothing of the student's to quote; only `withUnits` has their text.
            const withUnits = data.unitMode === 'withUnits';
            /* A facet for a box the question never shows resolves to nothing, so the facet is
               offered on exactly the test `evaluateNumerical` grades by — see `uncertaintyAsked`. */
            const withUncertainty = uncertaintyAsked(data);
            const parts = [
                num(ANSWER_FACET.value),
                ...(withUncertainty ? [num(ANSWER_FACET.uncertainty)] : []),
                ...(withUnits ? [txt(ANSWER_FACET.unit)] : []),
            ];
            // The whole is offered only where there are parts to combine — a lone value IS the whole,
            // and a second row saying so is the picker repeating itself.
            return {
                referenceable: true,
                facets: parts.length > 1 ? [...parts, txt(ANSWER_FACET.combined)] : parts,
            };
        }
        case 'slider':
            return {
                referenceable: true,
                facets: data.dualHandle
                    ? [num(ANSWER_FACET.low), num(ANSWER_FACET.high), txt(ANSWER_FACET.combined)]
                    : [num(ANSWER_FACET.value)],
            };
        case 'gps':
            return {
                referenceable: true,
                facets: [num(ANSWER_FACET.lat), num(ANSWER_FACET.lng), txt(ANSWER_FACET.combined)],
            };
        case 'fitb': {
            /*
             * Addressed by BLANK ID, never by position: inserting a blank in the middle of a
             * passage must not silently re-point every reference after it.
             *
             * AND A COMBINED FACET, which this deliberately did not have. The argument against was
             * that the blanks are separate questions sharing a passage, so joining them reads as
             * one answer — true of a passage whose blanks are unrelated, and wrong about the
             * commonest shape, which is a list broken into blanks. "You chose red, orange and
             * blue" is one answer about one thing, and writing it meant three references and the
             * punctuation between them by hand. The per-blank facets are all still there.
             */
            const blanks = data.blanks ?? [];
            return blanks.length > 0
                ? {
                    referenceable: true,
                    // The whole first, its parts under it — the order Numerical-with-units reads in.
                    facets: [txt(ANSWER_FACET.combined), ...blanks.map(b => txt(b.id))],
                }
                : no('noAnswer');
        }
        case 'ordering':
            return no('ordering');
        default:
            // Including every type this file has not met. A block type that cannot be referenced is
            // not something to offer and explain; it is something to leave out.
            return no('notAnInput');
    }
}
/** Whether `ref` still names something that exists — the cheap half of the status check. */
export function answerRefResolves(ref, blockType, blockData) {
    if (!blockType || !blockData)
        return false;
    const eligibility = answerRefEligibility(blockType, blockData);
    return eligibility.referenceable && eligibility.facets.some(f => f.id === ref.facet);
}
const EMPTY = { kind: 'empty' };
/**
 * THE SAME RESOLVED ANSWER AS A NUMBER, for a formula to compute with.
 *
 * `ResolvedAnswer` is display-shaped because that is what it was built for — a chip in a
 * sentence. An equation needs one number, and only three of the six shapes are one:
 *
 *   - `scalar`  — the ordinary case.
 *   - `measure` — the VALUE, without its unit. Safe only because the equation picker offers
 *     numeric facets alone: a Numerical in `withUnits` mode exposes `value` (the canonical
 *     number) separately from `full`, and `full` is text and never reaches here.
 *   - everything else — a range, a pair, a list and an empty answer are not a number, and
 *     guessing one from them (the midpoint? the first?) would be inventing data the teacher
 *     never wrote.
 *
 * It lives beside `resolveAnswerRef` so the display reading and the arithmetic reading of one
 * answer are decided in one file. Split across two, they drift — the lesson `DEGREE_READOUT_DECIMALS`
 * below is already here to record.
 */
export function resolvedAnswerNumber(resolved) {
    const text = resolved.kind === 'scalar' ? resolved.text
        : resolved.kind === 'measure' ? resolved.value
            // The VALUE, never the uncertainty beside it: arithmetic on `9.8 ± 0.3` means arithmetic on
            // 9.8, and a reference wanting the 0.3 asks for the uncertainty facet by name.
            : resolved.kind === 'uncertain' ? resolved.value
                // A point's ordinal IS its datum — the number is what a Likert analysis works on.
                : resolved.kind === 'point' ? resolved.value
                    : null;
    if (text === null || text.trim() === '')
        return null;
    const n = Number(text);
    // `Number`, not `parseFloat`: `parseFloat('12 apples')` is 12, and a recalled answer that is
    // not wholly a number is not a number. Half-reading one is how a wrong value grades silently.
    return Number.isFinite(n) ? n : null;
}
/**
 * How many decimals a degree readout carries.
 *
 * Exported so the UI's `fmtDegrees` and this agree by construction. They are the same claim
 * about the same instruments — one of them deciding differently is the drift that put
 * `183.4621` in a sentence quoting a block that was showing `183.5`.
 */
export const DEGREE_READOUT_DECIMALS = 1;
/**
 * Decimal places a GPS coordinate is shown to, which the UI's own constant has long said is
 * "everywhere it's shown" — and a reference quoting one is somewhere it is shown.
 *
 * Deliberately coarser than the 6 decimals stored and evaluated against: 5 is about 1.1 m,
 * which is roughly what a phone's GPS delivers, so no surface claims precision the hardware
 * does not have.
 */
export const COORD_READOUT_DECIMALS = 5;
/** A reading rounded the way its own block displays it, or left alone if it is not a number —
 *  a sensor that reported nothing must stay nothing, not become `0`. */
function asReadout(v, decimals) {
    const n = typeof v === 'number' ? v : NaN;
    if (!Number.isFinite(n))
        return v;
    const p = 10 ** decimals;
    return Math.round(n * p) / p;
}
const asDegreeReadout = (v) => asReadout(v, DEGREE_READOUT_DECIMALS);
const asCoordReadout = (v) => asReadout(v, COORD_READOUT_DECIMALS);
/** A stored value becomes display text AS THE STUDENT TYPED IT where it was text to begin with. */
/**
 * The most significant digits any answer is shown with, however many it arrives carrying.
 *
 * 12, matching `cleanComputed`, and for its stated reason: a double carries about 16 and the
 * noise lives in the last one or two, so nothing real needs a thirteenth. This is the ceiling,
 * not the usual case — almost every answer passes under it untouched.
 */
const DISPLAY_MAX_SIG_FIGS = 12;
/** Significant digits in a numeric string AS WRITTEN, which is not what the number knows. */
function sigFigsOf(text) {
    // The exponent says where the point sits, never how precisely it was stated.
    const mantissa = text.split(/[eE]/)[0] ?? '';
    const digits = mantissa.replace('-', '').replace('.', '');
    // Leading zeros place the number; they do not measure it. Trailing ones do, which is why this
    // trims only the front — `4.50` is three figures and must stay spelled that way.
    return digits.replace(/^0+/, '').length;
}
/**
 * A NUMBER'S TEXT, SHORTENED ONLY IF IT IS ABSURD.
 *
 * Two different things arrive here and only one may be touched. A value the student typed is
 * theirs, and the rule this module already follows for `4.50` is that it survives intact — so
 * anything at or under the ceiling is returned VERBATIM rather than parsed and reprinted, which
 * would quietly drop that trailing zero and restate their precision as lower than they wrote it.
 *
 * Above the ceiling nothing is being preserved. Nobody conveys precision with a forty-seventh
 * digit, and a student is free to type one: it is not an answer carrying more information, it is
 * a sentence a teacher can no longer read. Capping is the only part of this that reprints.
 *
 * Non-numeric text — a unit, a word, a chosen option — is returned untouched; this is reached by
 * every facet, and `m/s` has no significant figures.
 */
function cappedNumberText(text) {
    const n = Number(text);
    if (!Number.isFinite(n) || text.trim() === '')
        return text;
    return sigFigsOf(text) <= DISPLAY_MAX_SIG_FIGS ? text : String(parseFloat(n.toPrecision(DISPLAY_MAX_SIG_FIGS)));
}
function asText(v) {
    if (typeof v === 'string')
        return v.trim() === '' ? null : cappedNumberText(v);
    if (typeof v === 'number' && Number.isFinite(v))
        return cappedNumberText(String(v));
    return null;
}
const scalar = (v) => {
    const text = asText(v);
    return text === null ? EMPTY : { kind: 'scalar', text };
};
/**
 * Turn `{taskId, blockId, facet}` plus the stored answer into something renderable.
 *
 * `answer` is what the attempt log holds for that block — `lastAnswersFor(session, taskId)[blockId]`
 * — so nothing extra is stored anywhere for references to work. `blockData` is only consulted
 * for shape (is this slider dual?), never for the value.
 *
 * A NUMERICAL value comes back exactly as the student typed it: `4.50` stays `4.50`, and a pt-BR
 * comma decimal stays a comma. The echo shows a student their own answer, not a reformatted one.
 */
export function resolveAnswerRef(ref, answer, blockType, blockData) {
    if (answer === null || answer === undefined)
        return EMPTY;
    const a = (typeof answer === 'object' ? answer : {});
    const f = ref.facet;
    switch (blockType) {
        // A BARE VALUE, not the `*Answer` object the evaluator takes. What each block RECORDS and
        // what its evaluator accepts are two different shapes, and only the recorded one is in the
        // log — reading the interfaces instead cost a resolver that returned empty for half the
        // blocks, and the browser is what caught it.
        case 'keyword':
            return scalar(answer);
        case 'scale': {
            // The student's answer is the ORDINAL; the word is looked up from the block's own points,
            // so a relabelled scale re-reads every reference to it without any of them being touched.
            const value = asText(answer);
            if (value === null)
                return EMPTY;
            const point = (Array.isArray(blockData.values) ? blockData.values : [])
                .find((v) => String(v.value) === value);
            const label = typeof point?.label === 'string' && point.label.trim() !== '' ? point.label : null;
            if (f === ANSWER_FACET.label)
                return label === null ? EMPTY : { kind: 'scalar', text: label };
            if (f === ANSWER_FACET.combined) {
                // A point with no word of its own is just its number, the same way a numerical with no
                // unit is: showing nothing would hide an answer the student did give.
                return label === null ? { kind: 'scalar', text: value } : { kind: 'point', value, label };
            }
            return { kind: 'scalar', text: value };
        }
        /*
         * AS THE INSTRUMENT READS, which is not as the device computed.
         *
         * A lean or a heading is produced by hardware or by a simulated dial, so nobody typed it
         * and there is no "as the student wrote it" to preserve — the rule that keeps a typed
         * `4.50` intact has nothing to protect here. What arrives is a float: a tilt of 30 comes
         * back as 30.000000000000004, a heading as 183.4621.
         *
         * The blocks themselves already refuse to show that. Their readouts go through
         * `fmtDegrees`, which caps the decimals for a stated reason — no magnetometer or
         * inclinometer in a phone resolves better than about a degree, so the extra digits are
         * not merely noise, they misrepresent the instrument. A reference quoting the block was
         * the one surface printing them, which made the quote disagree with the thing quoted.
         */
        case 'tilt':
            return scalar(asDegreeReadout(answer));
        case 'compass':
            return scalar(asDegreeReadout(a.headingDegrees));
        case 'gps': {
            // The same rule the degree instruments follow above: a fix is produced by a receiver, so
            // there are no authored digits to protect and the raw float is not what the block shows.
            const lat = asText(asCoordReadout(a.lat));
            const lng = asText(asCoordReadout(a.lng));
            if (f === ANSWER_FACET.lat)
                return lat === null ? EMPTY : { kind: 'scalar', text: lat };
            if (f === ANSWER_FACET.lng)
                return lng === null ? EMPTY : { kind: 'scalar', text: lng };
            return lat === null || lng === null ? EMPTY : { kind: 'pair', a: lat, b: lng };
        }
        case 'numerical': {
            const value = asText(a.value);
            const unit = asText(a.unit);
            const uncertainty = asText(a.uncertainty);
            if (f === ANSWER_FACET.unit)
                return unit === null ? EMPTY : { kind: 'scalar', text: unit };
            if (f === ANSWER_FACET.uncertainty) {
                return uncertainty === null ? EMPTY : { kind: 'scalar', text: uncertainty };
            }
            if (f === ANSWER_FACET.combined) {
                // A value with no unit is still the value — the teacher asked for one, the student left
                // it off, and showing nothing would hide an answer they did give. The uncertainty is the
                // same bargain: whichever parts arrived are shown, and a missing one is simply absent.
                if (value === null)
                    return EMPTY;
                if (uncertainty !== null) {
                    return { kind: 'uncertain', value, uncertainty, ...(unit === null ? {} : { unit }) };
                }
                return unit === null ? { kind: 'scalar', text: value } : { kind: 'measure', value, unit };
            }
            return value === null ? EMPTY : { kind: 'scalar', text: value };
        }
        case 'slider': {
            // ALWAYS the pair, even on a single-handle slider — the block submits `{ a, b }` either
            // way and simply ignores `b` when there is one handle.
            const pair = a;
            const low = asText(pair.a?.value);
            const high = asText(pair.b?.value);
            const scalarOf = (text) => (text === null ? EMPTY : { kind: 'scalar', text });
            /*
             * `value` AND `low` ARE ONE ADDRESS, which is what keeps a reference alive across the
             * handle switch.
             *
             * A slider offers `value` with one handle and `low`/`high`/`combined` with two, so a
             * chip written against a single-handle slider named a facet that stopped existing the
             * moment a second handle was added — a live reference killed by a setting that did not
             * touch it. They are the same number either way: the first handle's.
             */
            const first = f === ANSWER_FACET.value || f === ANSWER_FACET.low;
            if (!blockData.dualHandle) {
                // And back the other way: `high` has no answer on one handle, so it says so rather
                // than quietly handing back the low one, which is a wrong answer wearing a right
                // one's clothes. `combined` is the whole answer, which here is the only value there is.
                if (f === ANSWER_FACET.high)
                    return EMPTY;
                return scalarOf(low);
            }
            if (first)
                return scalarOf(low);
            if (f === ANSWER_FACET.high)
                return scalarOf(high);
            return low === null || high === null ? EMPTY : { kind: 'range', low, high };
        }
        case 'fitb': {
            /*
             * THE WHOLE PASSAGE'S ANSWERS, in the order the blanks are authored in, joined the way
             * a list is spoken. `list` rather than a pre-joined string: the renderer already knows
             * how to say a list in the reader's language, and baking ", " here would be an English
             * comma in a Portuguese sentence.
             */
            if (f === ANSWER_FACET.combined) {
                const blanks = blockData.blanks ?? [];
                const items = blanks
                    .map(blank => asText(a[blank.id]))
                    .filter((text) => text !== null);
                if (items.length === 0)
                    return EMPTY;
                return items.length === 1 ? { kind: 'scalar', text: items[0] } : { kind: 'list', items };
            }
            // The facet IS the blank id, so a reordered passage still resolves.
            return scalar(a[f]);
        }
        case 'mcq':
        case 'dropdown': {
            /*
             * The chosen option's own TEXT, as the student saw it, recorded at submission.
             *
             * Not looked up from the option id against the block's current data: an option's text can
             * hold a variable chip, so two students saw different words, and recovering that later
             * means re-deriving the seed and re-running the roll. Captured once, when it was true.
             */
            const texts = a.selectedTexts;
            if (Array.isArray(texts)) {
                const items = texts.map(asText).filter((t) => t !== null);
                if (items.length === 0)
                    return EMPTY;
                // A multi-select yields ONE joined string and never per-selection facets: the subset is
                // the student's, so "the third one picked" is empty for anyone who picked two.
                return items.length === 1 ? { kind: 'scalar', text: items[0] } : { kind: 'list', items };
            }
            return scalar(texts);
        }
        default:
            return EMPTY;
    }
}
/**
 * Everything reachable from `from`, following the graph's own edges, with `skip` deleted.
 *
 * `skip` is a SET because dominance is asked of a set as often as of one node: an alias whose
 * members sit on three different pathways is covered when deleting all three disconnects the
 * referring task, while deleting any one of them leaves two routes open. One node is the
 * degenerate case, and callers that have one pass a set of one.
 */
/**
 * THE EDGES A STUDENT CAN ACTUALLY TRAVEL, which is not every edge the graph draws.
 *
 * `deriveGraph` over-approximates on purpose: an edge exists wherever a condition COULD route
 * along it, because proving a condition never fires is not generally possible. All Correct is
 * the one rung where it IS possible, and already proven elsewhere — `starvedBlockCauses` asks
 * the reachability modules whether any answer can reach a block's pass state, and the Flow
 * panel and the connections diagram both act on the answer.
 *
 * Reported from a three-way split whose dropdown routes every option by rule: targeting all
 * three answers starves All Correct, so the splitter's positional `next` can never fire — and
 * the graph still drew it straight past the three pathways to the task that reunites them. An
 * alias over all three arrivals was therefore "conditional", and the panel said *"Students on
 * arrive with no answer"* with no route to name, because the route it had found holds no task.
 * The owner, correctly: *"there should be no warnings because all paths are covered."*
 *
 * IT NARROWS A GUARANTEE, so it is worth saying why that is safe here. Everywhere else the
 * over-approximation is the safe direction — an extra edge makes domination harder to prove, so
 * an `always` is trustworthy. Dropping one makes `always` easier, which is only sound if the
 * edge truly cannot fire. This one cannot: `starvedBlockCauses` is a proof over the block's own
 * answers, the same proof that dims the rung's card and takes the branch off the drawing.
 *
 * TASKS WITHOUT BLOCKS KEEP EVERY EDGE. `GraphTask` does not carry them and several callers
 * pass that shape, so an absent block list means "not known" and leaves the behaviour exactly as
 * it was — never "nothing is starved".
 */
function routeMap(tasks) {
    const graph = deriveGraph(tasks);
    const starved = new Set();
    for (const t of tasks) {
        if (t.blocks && starvedBlockCauses(t.blocks).length > 0)
            starved.add(t.id);
    }
    const out = new Map();
    for (const e of graph.edges) {
        if (!e.toId)
            continue; // a loose thread goes nowhere, so it carries no student
        if (e.cat === 'route' && starved.has(e.fromId))
            continue;
        (out.get(e.fromId) ?? out.set(e.fromId, []).get(e.fromId)).push(e.toId);
    }
    return out;
}
function reachable(from, out, skip) {
    const seen = new Set();
    if (skip?.has(from))
        return seen;
    const queue = [from];
    seen.add(from);
    while (queue.length > 0) {
        for (const next of out.get(queue.pop()) ?? []) {
            if (skip?.has(next) || seen.has(next))
                continue;
            seen.add(next);
            queue.push(next);
        }
    }
    return seen;
}
export function answerRefReach(sourceTaskId, referringTaskId, tasks, site) {
    // See `ReachSite.atExit`. At the exit the general traversal below answers it correctly on its
    // own — `reachable` includes the node it starts from, so a task dominates every route to
    // itself and an unreachable one still reads `never` through the ordinary checks.
    if (sourceTaskId === referringTaskId && !site?.atExit)
        return 'never';
    const entry = deriveGraph(tasks).spine[0];
    if (!entry)
        return 'never';
    const out = routeMap(tasks);
    const fromEntry = reachable(entry, out);
    // An unreachable referring task renders nothing at all, so its references cannot fire. Saying
    // `never` is honest; the problem is the task, and the board already reports that separately.
    if (!fromEntry.has(referringTaskId) || !fromEntry.has(sourceTaskId))
        return 'never';
    if (!reachable(sourceTaskId, out).has(referringTaskId))
        return 'never';
    return reachable(entry, out, new Set([sourceTaskId])).has(referringTaskId) ? 'sometimes' : 'always';
}
/**
 * THE SAME QUESTION ASKED OF A SET: do these sources, BETWEEN THEM, cover every route here?
 *
 * One member can fail to dominate while several together cannot be avoided — three pathways,
 * each ending in its own copy of the question, the three answers joined under one alias. Asking
 * per member and taking the best answer calls that `sometimes`, which is true of each member and
 * false of the alias, and the alias is what the sentence reads.
 *
 * Same traversal as the single case and the same over-approximation, so the same safe direction:
 * an edge exists wherever a condition COULD route along it, which can only make domination harder
 * to prove, never easier. A claimed `always` is therefore trustworthy; a `sometimes` may be a
 * route nobody takes.
 *
 * NOT IN THE DELETION SET: a source that cannot reach the referring task at all. It contributes
 * no coverage — a student passing through it still arrives with nothing — and deleting it could
 * disconnect the graph for a reason that has nothing to do with this alias.
 */
export function answerRefJointReach(sourceTaskIds, referringTaskId, tasks) {
    const entry = deriveGraph(tasks).spine[0];
    if (!entry)
        return 'never';
    const out = routeMap(tasks);
    const fromEntry = reachable(entry, out);
    if (!fromEntry.has(referringTaskId))
        return 'never';
    // A source earns its place in the deletion set by being reachable, being able to get here, and
    // not being here — the same three guards the single-source case applies, per source.
    const contributing = sourceTaskIds.filter(id => id !== referringTaskId && fromEntry.has(id) && reachable(id, out).has(referringTaskId));
    if (contributing.length === 0)
        return 'never';
    return reachable(entry, out, new Set(contributing)).has(referringTaskId) ? 'sometimes' : 'always';
}
/**
 * The one place the two halves of "is this reference healthy" are combined.
 *
 * Split out so the census — which memoises reach and so cannot call `answerRefStatus` without
 * throwing that away — still shares the RULE rather than restating it. The rule is the part that
 * would drift; the lookups around it are not.
 */
function statusFrom(resolves, reach) {
    if (!resolves || reach === 'never')
        return 'broken';
    return reach === 'sometimes' ? 'conditional' : 'ok';
}
/** The one derivation of a reference's health. Everything that shows a status calls this. */
export function answerRefStatus(ref, referringTaskId, tasks, site) {
    const source = tasks.find(t => t.id === ref.taskId);
    const block = source?.blocks?.find(b => b.id === ref.blockId);
    const resolves = answerRefResolves(ref, block?.type, block?.data ?? undefined);
    return statusFrom(resolves, resolves ? answerRefReach(ref.taskId, referringTaskId, tasks, site) : 'never');
}
/** A task as the census reads it: the graph's view, plus the blocks whose prose it has to search. */
/**
 * WHAT KIND OF THING AN ALIAS QUOTES, across all of its addresses.
 *
 * `AnswerFacet.kind` is per facet and has been there since the beginning; this is the question
 * a teacher actually asks, which is about the NAME rather than one of its addresses. It decides
 * whether the alias can enter an equation at all, and until now that was invisible until the
 * formula silently yielded nothing.
 *
 * `mixed` is reachable and is almost always a mistake. The one structure that legitimately
 * joins several addresses under one name — three pathways, each a different route to the same
 * question, joined so the alias covers every path — joins members of ONE kind, because they are
 * the same question asked three ways. Members of different kinds means the name spans different
 * question types, so it is warned about rather than forbidden: the house rule is to warn when in
 * doubt, and a teacher may know something the system does not.
 *
 * `null` for an alias whose addresses all fail to resolve — there is nothing to be the kind OF,
 * and that is already reported as broken by `aliasStatus`.
 */
export function aliasKind(alias, tasks) {
    const kinds = new Set();
    for (const m of alias.members) {
        const block = tasks.find(t => t.id === m.taskId)?.blocks?.find(b => b.id === m.blockId);
        if (!block)
            continue;
        const eligibility = answerRefEligibility(block.type, (block.data ?? {}));
        if (!eligibility.referenceable)
            continue;
        const kind = eligibility.facets.find(f => f.id === m.facet)?.kind;
        if (kind)
            kinds.add(kind);
    }
    if (kinds.size === 0)
        return null;
    return kinds.size > 1 ? 'mixed' : [...kinds][0];
}
/**
 * A chip's opening tag, which is the only place its attributes can be.
 *
 * Minted per use rather than shared. A `/g` regex carries `lastIndex`, and two functions reading
 * one object is a state bug waiting for the day someone reaches for `.test()` or `.exec()`.
 */
const CHIP_TAG_SOURCE = String.raw `<span\b[^>]*\bdata-answer-ref-block=[^>]*>`;
const chipTags = () => new RegExp(CHIP_TAG_SOURCE, 'g');
/**
 * The WHOLE element, opening tag through closing, for a rewrite that has to reach the text.
 *
 * A chip is an atom, so there is nothing nested to worry about and `[^<]*` is the whole of its
 * content. The census only ever needs the attributes and uses `chipTags` above; a re-point
 * needs both halves — see `repointAnswerRefs`.
 */
const chipElements = () => new RegExp(CHIP_TAG_SOURCE + String.raw `[^<]*<\/span>`, 'g');
const attr = (tag, name) => new RegExp(`\\b${name}="([^"]*)"`).exec(tag)?.[1] ?? null;
/**
 * Every reference in the experience, in document order, each with its health.
 *
 * ONE pass, read by two surfaces that must not disagree: the manager lists these rows, and
 * publication gating asks whether any of them is `broken`. A second enumeration for the gate
 * would be a second thing to keep honest, and the failure mode — publishing an experience the
 * manager is showing a red row for — is exactly the kind nobody notices until a student hits it.
 *
 * Reach is memoised per task PAIR because that is what it depends on: a block's position has no
 * bearing on it, so a task holding twenty chips that all point into the same earlier task costs
 * one traversal rather than twenty.
 */
export function collectAnswerRefs(tasks) {
    const sites = [];
    const reachCache = new Map();
    const reachOf = (sourceId, inId) => {
        const key = `${sourceId}>${inId}`;
        let got = reachCache.get(key);
        if (got === undefined)
            reachCache.set(key, (got = answerRefReach(sourceId, inId, tasks)));
        return got;
    };
    for (const task of tasks) {
        for (const block of task.blocks ?? []) {
            const walk = (value, path) => {
                if (typeof value === 'string') {
                    if (!value.includes('data-answer-ref-block'))
                        return;
                    for (const [tag] of value.matchAll(chipTags())) {
                        const blockId = attr(tag, 'data-answer-ref-block');
                        if (!blockId)
                            continue;
                        const ref = {
                            taskId: attr(tag, 'data-answer-ref-task') ?? '',
                            blockId,
                            facet: attr(tag, 'data-answer-ref-facet') ?? ANSWER_FACET.value,
                        };
                        const source = tasks.find(t => t.id === ref.taskId);
                        const target = source?.blocks?.find(b => b.id === ref.blockId);
                        const resolves = answerRefResolves(ref, target?.type, target?.data ?? undefined);
                        const status = statusFrom(resolves, resolves ? reachOf(ref.taskId, task.id) : 'never');
                        sites.push({
                            ref, inTaskId: task.id, inBlockId: block.id, path,
                            label: attr(tag, 'data-answer-ref-label'), status,
                        });
                    }
                    return;
                }
                if (Array.isArray(value)) {
                    value.forEach((v, i) => walk(v, [...path, i]));
                    return;
                }
                if (value && typeof value === 'object') {
                    for (const [k, v] of Object.entries(value))
                        walk(v, [...path, k]);
                }
            };
            walk(block.data ?? {}, []);
        }
    }
    return sites;
}
/**
 * Whether publication has to stop, and why.
 *
 * `broken` blocks; `conditional` does not, for the reason `AnswerRefReach` gives — the system
 * knows a source is only sometimes reached but not whether that was intended, and refusing to
 * publish over it would refuse every legitimate conditional reference ever authored.
 */
export function brokenAnswerRefs(tasks) {
    return collectAnswerRefs(tasks).filter(s => s.status === 'broken');
}
/**
 * Point every chip that names `from` at `to` instead, returning new data or the same object.
 *
 * Matched by ADDRESS, not by position. Bulk re-point is always "everything that pointed at the
 * block I deleted should point at this one now" — the teacher is thinking about the target, not
 * about which paragraph each chip happens to sit in. Matching by path would also break the
 * moment anything reorders.
 *
 * The LABEL is left alone. A chip the teacher named keeps its name, because they wrote that name
 * for the sentence it sits in; a chip they did not name has no label to carry and goes straight
 * back to deriving one from its new target.
 *
 * Returns the identical object when nothing matched, so a caller can use it as the "did anything
 * change" test and skip the save.
 */
export function repointAnswerRefs(data, from, to, 
/**
 * The new target's derived name, for the chip's context-free fallback text.
 *
 * Supplied by the caller because deriving it needs i18n, which this package does not have.
 * Omitting it CLEARS the fallback rather than leaving the old one: after a deliberate
 * repoint, a name that still reads as the block the teacher just moved away from is worse
 * than a chip with no fallback at all.
 */
fallback) {
    let touched = false;
    const rewrite = (html) => html.replace(chipElements(), element => {
        const tag = chipTags().exec(element)?.[0] ?? '';
        if (attr(tag, 'data-answer-ref-task') !== from.taskId)
            return element;
        if (attr(tag, 'data-answer-ref-block') !== from.blockId)
            return element;
        if ((attr(tag, 'data-answer-ref-facet') ?? ANSWER_FACET.value) !== from.facet)
            return element;
        touched = true;
        const moved = tag
            .replace(/\bdata-answer-ref-task="[^"]*"/, `data-answer-ref-task="${to.taskId}"`)
            .replace(/\bdata-answer-ref-block="[^"]*"/, `data-answer-ref-block="${to.blockId}"`)
            .replace(/\bdata-answer-ref-facet="[^"]*"/, `data-answer-ref-facet="${to.facet}"`);
        const cleared = moved.replace(/\s*\bdata-answer-ref-fallback="[^"]*"/, '');
        const opening = fallback
            ? cleared.replace(/>$/, ` data-answer-ref-fallback="${fallback}">`)
            : cleared;
        /*
         * THE TEXT MOVES WITH THE ADDRESS.
         *
         * Rewriting the attributes alone left every raw-render surface showing the name of the
         * block the teacher had just moved AWAY from, while the chip resolved to the new one —
         * caught in the browser, with the two re-pointed chips reading "Gas name" above
         * attributes that said `blk_kw…`. The attributes are the identity and the text is what a
         * reader sees, so a rewrite that touches one and not the other is always wrong.
         */
        return `${opening}${fallback ?? ''}</span>`;
    });
    const walk = (value) => {
        if (typeof value === 'string') {
            return value.includes('data-answer-ref-block') ? rewrite(value) : value;
        }
        if (Array.isArray(value))
            return value.map(walk);
        if (value && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, walk(v)]));
        }
        return value;
    };
    const next = walk(data);
    return touched ? next : data;
}
//# sourceMappingURL=answer-ref.js.map